/**
 * JWT Utility Functions
 * Decodes JWT tokens and provides expiration checking
 */

/**
 * Decode JWT token payload (no library needed - JWT is base64 encoded JSON)
 * @param {string} token - JWT token string
 * @returns {object|null} - Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode base64 payload (second part)
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} - True if expired or invalid, false if valid
 */
export const isTokenExpired = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const now = Date.now();

  return now >= expirationTime;
};

/**
 * Get token expiration time in milliseconds
 * @param {string} token - JWT token string
 * @returns {number|null} - Expiration time in milliseconds or null if invalid
 */
export const getTokenExpiration = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return null;
  }

  return payload.exp * 1000;
};

/**
 * Get time until token expires in milliseconds
 * @param {string} token - JWT token string
 * @returns {number|null} - Milliseconds until expiration or null if invalid/expired
 */
export const getTimeUntilExpiration = (token) => {
  const expirationTime = getTokenExpiration(token);
  if (!expirationTime) {
    return null;
  }

  const now = Date.now();
  const timeUntilExpiration = expirationTime - now;

  return timeUntilExpiration > 0 ? timeUntilExpiration : null;
};

/**
 * Calculate when to refresh token (proactive refresh)
 * Refresh 5-10 minutes before expiration, or at 80% of token lifetime
 * @param {string} token - JWT token string
 * @param {number} refreshBeforeMs - Milliseconds before expiration to refresh (default: 5 minutes)
 * @returns {number|null} - Milliseconds until refresh should occur or null if invalid
 */
export const getRefreshTimer = (token, refreshBeforeMs = 5 * 60 * 1000) => {
  const expirationTime = getTokenExpiration(token);
  if (!expirationTime) {
    return null;
  }

  const now = Date.now();
  const timeUntilExpiration = expirationTime - now;

  if (timeUntilExpiration <= 0) {
    return null; // Token already expired
  }

  // Refresh before expiration (default 5 minutes, or at 80% of lifetime)
  const tokenLifetime = timeUntilExpiration;
  const refreshAt80Percent = tokenLifetime * 0.2; // 20% remaining
  const refreshTime = Math.min(refreshBeforeMs, refreshAt80Percent);

  const timeUntilRefresh = timeUntilExpiration - refreshTime;

  return timeUntilRefresh > 0 ? timeUntilRefresh : 0; // Refresh now if very close to expiration
};

